import type { Opcao } from './useFacetaOptions'

/** Puxa a próxima página da faceta de funcionário e devolve o que veio nela. */
export type CarregarMaisFuncionarios = () => Promise<Opcao[]>

/** Sentido do passo na lista: `1` avança, `-1` volta. */
export type PassoNavegacao = 1 | -1

type LimitesNavegacao = {
    isPrimeiro: boolean
    isUltimo: boolean
}

/**
 * Devolve a opção vizinha do funcionário atual, no sentido pedido.
 *
 * @returns `null` quando não há atual, quando ele não está na lista ou quando
 * a ponta da lista foi alcançada.
 */
export function encontrarVizinho(
    opcoes: Opcao[],
    atualId: number | null,
    passo: PassoNavegacao,
): Opcao | null {
    if (atualId == null) return null

    const indice = opcoes.findIndex(opcao => opcao.id === atualId)
    if (indice === -1) return null

    return opcoes[indice + passo] ?? null
}

/**
 * Acumula páginas da faceta até o funcionário atual aparecer na lista.
 *
 * O funcionário escolhido pela busca costuma estar fora das páginas carregadas
 * — a busca é do servidor e some junto com o painel, então a lista volta ao
 * começo sem ele. Sem localizá-lo não há vizinho para onde navegar.
 *
 * Para de puxar páginas quando uma delas não traz nenhum id inédito, mesmo sem
 * ter encontrado o atual: ele pode não pertencer à lista do escopo.
 */
export async function carregarAtePosicionar(
    opcoes: Opcao[],
    atualId: number | null,
    carregarMais?: CarregarMaisFuncionarios,
): Promise<Opcao[]> {
    if (atualId == null || !carregarMais) return opcoes

    let lista = opcoes

    while (!lista.some(opcao => opcao.id === atualId)) {
        const novos = await carregarMais()
        const ineditos = novos.filter(
            novo => !lista.some(opcao => opcao.id === novo.id),
        )
        if (!ineditos.length) return lista

        lista = [...lista, ...ineditos]
    }

    return lista
}

/**
 * Diz quais setas ficam travadas para o funcionário atual.
 *
 * Atual fora das páginas carregadas libera as duas: a posição dele só é
 * conhecida depois de paginar, e travar aqui deixaria a navegação morta.
 */
export function calcularLimitesNavegacao({
    opcoes,
    atualId,
    temMais,
}: {
    opcoes: Opcao[]
    atualId: number | null
    temMais?: boolean
}): LimitesNavegacao {
    if (atualId == null) return { isPrimeiro: true, isUltimo: true }

    if (!opcoes.length) return { isPrimeiro: true, isUltimo: true }

    const indice = opcoes.findIndex(opcao => opcao.id === atualId)
    if (indice === -1) return { isPrimeiro: false, isUltimo: false }

    return {
        isPrimeiro: indice === 0,
        isUltimo: indice === opcoes.length - 1 && !temMais,
    }
}
